export const MAP_SETTINGS_KEY = 'hiiker_map_settings';

export type MapProjection = 'mercator' | 'globe';

export interface MapSettings {
  lockToNorth: boolean;
  projection: MapProjection;
  elevationExaggeration: number;
  /** "Lock to 1:50k" on National+ — see applyNationalScaleLock. */
  lockNationalTo50k: boolean;
}

export const DEFAULT_MAP_SETTINGS: MapSettings = {
  lockToNorth: false,
  projection: 'globe',
  elevationExaggeration: 1.0,
  lockNationalTo50k: false,
};

// ─── National+ scale lock ────────────────────────────────────────────────────
//
// National+ draws from one raster source, `national-overlay`, mosaicked by our
// tileserver: up to z13 it hands back the 1:50k national series, from z14 it
// switches to the 1:25k series. So there is no layer to toggle — the only lever
// is the source's `maxzoom`. Capping it at 13 makes Mapbox overzoom the 1:50k
// tiles rather than request the 1:25k ones, which is precisely how the separate
// "National+ (UK 1:50k)" style differs from National+ (identical JSON, maxzoom
// 13 vs 16) and what "Lock to 1:50k" does in the apps.
//
// Mapbox has no setter for a live source's maxzoom, so the source is removed and
// re-added; its layers go back in reverse order so each one's `beforeId` is
// present again by the time it is added.

export const NATIONAL_OVERLAY_SOURCE_ID = 'national-overlay';
export const NATIONAL_SCALE_LOCK_MAX_ZOOM = 13;
/** Fallback when we never saw the style in its authored (unclamped) state. */
export const NATIONAL_OVERLAY_DEFAULT_MAX_ZOOM = 16;

/** Where we stash a style's authored maxzoom so unlocking can restore it. */
const AUTHORED_MAX_ZOOM_KEY = '__hiikerNationalAuthoredMaxZoom';

export interface NationalLayerPlacement {
  layer: Record<string, any>;
  /** Id of the layer this one sits before, or undefined if it is last. */
  beforeId?: string;
}

/**
 * The layers drawing `sourceId`, each paired with the layer it sits before, so
 * they can be put back exactly where they were.
 */
export function nationalOverlayLayerPlan(
  layers: Array<Record<string, any>>,
  sourceId: string = NATIONAL_OVERLAY_SOURCE_ID,
): NationalLayerPlacement[] {
  const plan: NationalLayerPlacement[] = [];
  layers.forEach((layer, index) => {
    if (layer.source !== sourceId) return;
    plan.push({ layer, beforeId: layers[index + 1]?.id });
  });
  return plan;
}

/**
 * The maxzoom the source should be sitting at. `authoredMaxZoom` is whatever the
 * style shipped with, remembered from before we first clamped it.
 */
export function resolveNationalMaxZoom(locked: boolean, authoredMaxZoom?: number): number {
  if (locked) return NATIONAL_SCALE_LOCK_MAX_ZOOM;
  return authoredMaxZoom ?? NATIONAL_OVERLAY_DEFAULT_MAX_ZOOM;
}

export function applyNationalScaleLock(map: any, locked: boolean): void {
  if (!map) return;

  let style: any;
  try { style = map.getStyle?.(); } catch { return; }

  const source = style?.sources?.[NATIONAL_OVERLAY_SOURCE_ID];
  // Every other base style is a no-op: no national-overlay source, nothing to cap.
  if (!source) return;

  const current = source.maxzoom;
  // A maxzoom that isn't ours is the style in its authored state, so it is the
  // value to hand back on unlock. Recording it on every pass keeps us right
  // across style switches without needing to know which style is loaded.
  if (current !== NATIONAL_SCALE_LOCK_MAX_ZOOM) map[AUTHORED_MAX_ZOOM_KEY] = current;

  const target = resolveNationalMaxZoom(locked, map[AUTHORED_MAX_ZOOM_KEY]);
  if (target === current) return;

  const plan = nationalOverlayLayerPlan(style.layers || []);
  try {
    plan.forEach(({ layer }) => map.removeLayer(layer.id));
    map.removeSource(NATIONAL_OVERLAY_SOURCE_ID);
    map.addSource(NATIONAL_OVERLAY_SOURCE_ID, { ...source, maxzoom: target });
    [...plan].reverse().forEach(({ layer, beforeId }) => {
      map.addLayer(layer, beforeId && map.getLayer?.(beforeId) ? beforeId : undefined);
    });
  } catch {}
}

export function getMapSettings(): MapSettings {
  try {
    const raw = localStorage.getItem(MAP_SETTINGS_KEY);
    if (raw) return { ...DEFAULT_MAP_SETTINGS, ...JSON.parse(raw) };
  } catch {}
  return { ...DEFAULT_MAP_SETTINGS };
}

export function saveMapSettings(settings: MapSettings): void {
  try {
    localStorage.setItem(MAP_SETTINGS_KEY, JSON.stringify(settings));
  } catch {}
}

export function applyMapSettings(map: any, settings: MapSettings): void {
  // Lock to north
  if (settings.lockToNorth) {
    map.setBearing?.(0);
    map.dragRotate?.disable();
    map.touchZoomRotate?.disableRotation();
  } else {
    map.dragRotate?.enable();
    map.touchZoomRotate?.enableRotation();
  }

  // Projection
  try { map.setProjection(settings.projection); } catch {}

  // Elevation exaggeration (only when terrain is active)
  try {
    if (map.getTerrain()) {
      map.setTerrain({ source: 'mapbox-dem', exaggeration: settings.elevationExaggeration });
    }
  } catch {}

  // National+ 1:50k lock
  applyNationalScaleLock(map, settings.lockNationalTo50k);
}
